/**
 * Runtime — dependency injection for shared operations.
 *
 * Operations use getDb(), callUndo(), getNow() instead of importing
 * singletons directly. This allows the same code to run in:
 *
 * - **Client**: setOpsContext({ db, registerUndo }) at app startup
 * - **Server**: setContextResolver() that reads from AsyncLocalStorage
 *
 * Function signatures in the operations layer stay unchanged —
 * no `ctx` first parameter, just call getDb() internally.
 */

import type { TotodoDb } from "./db-types.js"
import { isValidTimeZone } from "./dayTime.js"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UndoEntryType = "create" | "update" | "delete" | "batch"

export type RegisterUndoFn = (
  description: string,
  undo: () => Promise<void>,
  redo: () => Promise<void>,
  type?: UndoEntryType,
) => void

export interface OpEvent {
  name: string
  data: Record<string, string | number | boolean>
}

export type OpEventListener = (event: OpEvent) => void

export interface OpsContext {
  db: TotodoDb
  registerUndo?: RegisterUndoFn
  onEvent?: OpEventListener
  /**
   * The clock getNow() reads, in ms since the epoch (default Date.now). The Durable Object supplies a
   * strictly increasing one: its own Date.now() does not advance while code runs without I/O.
   */
  now?: () => number
  /**
   * The IANA zone ("Europe/Stockholm") whose calendar days the operations use: where a day starts and
   * ends for Today / Evening / Upcoming, the evening hour, the day a routine's step belongs to.
   * Absent = the runtime's own local zone, which is what the app (SPA) wants and never sets.
   * The server's runtime is UTC, so it supplies the user's zone - see getOpsTimeZone().
   */
  timeZone?: string
  /**
   * Looks the zone up when it is not known up front (the server reads it from the user's devices).
   * Called by getOpsTimeZone() only when `timeZone` is absent; the implementation caches per request.
   */
  resolveTimeZone?: () => Promise<string | undefined>
}

// ---------------------------------------------------------------------------
// Context storage
// ---------------------------------------------------------------------------

/** Default context — set once at client startup */
let _defaultCtx: OpsContext | null = null

/** Context resolver — called per-access, for server per-request routing */
let _contextResolver: (() => OpsContext | undefined) | null = null

/**
 * Guest (read-only) mode flag.
 * When enabled, getDb() returns a proxy that silently blocks all writes.
 * Set via setGuestMode(true) on the guest share page.
 */
let _guestMode = false

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Set the default operations context.
 * Call once at app startup (client) with { db, registerUndo }.
 */
export function setOpsContext(ctx: OpsContext): void {
  _defaultCtx = ctx
}

/**
 * Set a context resolver for per-request routing (server).
 * The resolver is called on every getDb()/callUndo() access.
 * Returns undefined when not in a request context → falls back to default.
 */
export function setContextResolver(
  resolver: () => OpsContext | undefined,
): void {
  _contextResolver = resolver
}

/**
 * Enable or disable guest (read-only) mode.
 * When enabled, all table write methods (put, add, update, delete, etc.)
 * become silent no-ops. Reads work normally.
 */
export function setGuestMode(enabled: boolean): void {
  _guestMode = enabled
}

export function isGuestMode(): boolean {
  return _guestMode
}

// ---------------------------------------------------------------------------
// Accessors (used by operation modules)
// ---------------------------------------------------------------------------

function getOpsContext(): OpsContext {
  if (_contextResolver) {
    const resolved = _contextResolver()
    if (resolved) return resolved
  }
  if (_defaultCtx) return _defaultCtx
  throw new Error(
    "OpsContext not configured. Call setOpsContext() or setContextResolver().",
  )
}

// Write methods on Dexie Table that must be blocked in guest mode
const WRITE_METHODS = new Set([
  "put", "add", "update", "delete", "bulkPut", "bulkAdd",
  "bulkUpdate", "bulkDelete", "modify", "clear",
])

// Silent no-op that resolves to 0 / undefined — matches Dexie return types
const WRITE_NOOP = () => Promise.resolve(0 as never)

/**
 * Get the Dexie database instance for the current context.
 * In guest mode, returns a proxy that silently blocks write methods.
 */
export function getDb(): TotodoDb {
  const db = getOpsContext().db
  if (!_guestMode) return db

  // Return a proxy that intercepts table access
  return new Proxy(db, {
    get(target, prop, receiver) {
      const val = Reflect.get(target, prop, receiver)
      // If the accessed property is a Dexie Table, wrap it
      if (val && typeof val === "object" && typeof val.put === "function") {
        return new Proxy(val, {
          get(tgt, method) {
            if (typeof method === "string" && WRITE_METHODS.has(method)) {
              return WRITE_NOOP
            }
            return Reflect.get(tgt, method)
          },
        })
      }
      return val
    },
  }) as TotodoDb
}

/**
 * Call the undo registration function if configured.
 * No-op when registerUndo is not provided (server mode).
 */
export function callUndo(
  description: string,
  undo: () => Promise<void>,
  redo: () => Promise<void>,
  type?: UndoEntryType,
): void {
  const fn = getOpsContext().registerUndo
  if (fn) fn(description, undo, redo, type)
}

/**
 * Get current UTC timestamp as stringified milliseconds.
 * Matches the client's dayjs.utc().valueOf().toString() format.
 *
 * Reads the context's `now` clock when it has one (the server's Durable Object, see OpsContext.now),
 * else Date.now(): the browser's clock advances by itself.
 */
export function getNow(): string {
  let now: number | undefined
  try {
    now = getOpsContext().now?.()
  } catch {
    // No context configured (tests, lightweight callers): the plain clock
  }
  return (now ?? Date.now()).toString()
}

const validZone = (zone: string | undefined): string | undefined =>
  isValidTimeZone(zone) ? zone : undefined

/**
 * The time zone the operations read calendar days in, or `undefined` for the runtime's own local zone
 * (the SPA: it sets neither `timeZone` nor `resolveTimeZone`, so nothing changes for it). An unknown
 * zone name, a failing lookup and a missing context all mean `undefined`.
 *
 * The zone ITSELF whenever no lookup is needed (the app, a context with `timeZone`); a promise only
 * when `resolveTimeZone` has to run (the server). Operations read it as
 *
 *   const zone = getOpsTimeZoneOrLookup()
 *   const timeZone = zone instanceof Promise ? await zone : zone
 *
 * never as `await getOpsTimeZone()`: the app runs the views as Dexie live queries, and a live query
 * whose querier is not itself an async function loses its zone at a native `await` of anything that
 * is not a Dexie promise - even `await undefined`. Every read after it goes unobserved, so the open
 * view never re-runs on a synced change (Today on totodo.io, 2026-10-08; test/viewLiveQuery.test.ts).
 */
export function getOpsTimeZoneOrLookup(): string | undefined | Promise<string | undefined> {
  let ctx: OpsContext
  try {
    ctx = getOpsContext()
  } catch {
    return undefined
  }
  if (ctx.timeZone !== undefined || !ctx.resolveTimeZone) return validZone(ctx.timeZone)
  try {
    return Promise.resolve(ctx.resolveTimeZone()).then(validZone, () => undefined)
  } catch {
    return undefined
  }
}

/**
 * getOpsTimeZoneOrLookup() as a promise, for code that never runs inside a live query (server
 * modules). Shared operations use getOpsTimeZoneOrLookup().
 */
export async function getOpsTimeZone(): Promise<string | undefined> {
  return getOpsTimeZoneOrLookup()
}

/**
 * Emit an analytics/telemetry event via the DI-configured listener.
 * Fire-and-forget: never blocks, never throws.
 * No-op when onEvent is not configured (tests, lightweight contexts).
 */
export function emitEvent(name: string, data: Record<string, string | number | boolean>): void {
  try {
    const fn = getOpsContext().onEvent
    if (fn) fn({ name, data })
  } catch {
    // Non-critical — swallow. DI context may not be available in edge cases.
  }
}
